import { shallowEqual } from "react-redux";

// Spark components
import { GuxButton, GuxIcon, GuxTooltip } from "genesys-spark-components-react";
import { toGuxDisabled } from "util/componentUtil";

// Common
import { DefaultLimits } from "constants/DefaultLimits";
import { useStrings } from "features/i18n/useStrings";
import { useAppDispatch, useAppSelector } from "app/hooks";
import AutomatedScoringType from "enums/AutomatedScoringType";

// Feature slice
import {
    qualityFormActions,
    selectAutomatedScoringOption,
} from "features/qualityForm/qualityFormSlice";
import { selectQuestion } from "features/qualityForm/qualityFormSlices/qualityFormCommons";

// Validations
import {
    hasMaximumAIScoringAnswerOptions,
    selectIsQuestionValidForUserDefinedDefaultAnswer,
} from "features/qualityForm/qualityFormValidations";
import {
    hasMinimumAnswerOptions,
    hasMaximumAnswerOptions,
} from "features/qualityForm/qualityFormValidations/qualityFormAnswerValidations";

// Question common
import { PropertySection } from "../common/PropertySection";
import { AnswerOption } from "./AnswerOptionInput";

// Strings
import strings from "./Question.strings";
import { ErrorStatement } from "../common/ErrorStatement/ErrorStatement";

export default function AnswerOptionsSection({
    questionId,
}: {
    questionId: string;
}) {
    const dispatch = useAppDispatch();
    const t = useStrings(strings);

    const answerOptions = useAppSelector((state) => {
        const question = selectQuestion(state, questionId);
        return question?.answerOptions;
    }, shallowEqual);

    // Move these selectors from QuestionEditor into this component
    const hasEnoughAnswerOptions = useAppSelector((state) =>
        hasMinimumAnswerOptions(state, questionId)
    );

    const hasTooManyAnswerOptions = useAppSelector((state) =>
        hasMaximumAnswerOptions(state, questionId)
    );

    const hasTooManyAIScoringAnswerOptions = useAppSelector((state) => {
        return (
            selectAutomatedScoringOption(state, questionId) ===
                AutomatedScoringType.AI_SCORING &&
            hasMaximumAIScoringAnswerOptions(state, questionId)
        );
    });

    const { isCritical } = useAppSelector((state) => {
        const question = selectQuestion(state, questionId);
        return {
            isCritical: question?.isCritical,
        };
    }, shallowEqual);

    const disabled =
        isCritical ||
        hasTooManyAnswerOptions ||
        hasTooManyAIScoringAnswerOptions;

    const showDefaultAnswerRequiredError = !useAppSelector((state) =>
        selectIsQuestionValidForUserDefinedDefaultAnswer(state, questionId)
    );

    return (
        <PropertySection title={t.labels.answerOptions}>
            {answerOptions?.map((value, index) => {
                return (
                    <AnswerOption
                        answerOptionId={value}
                        questionId={questionId}
                        canDeleteAnswer={hasEnoughAnswerOptions}
                        answerIndex={index}
                        key={value}
                    />
                );
            })}
            {showDefaultAnswerRequiredError && (
                <span data-testid="default-answer-required">
                    <ErrorStatement
                        errorMessage={
                            t.errors.defaultAnswerRequiredForUserDefined
                        }
                    />
                </span>
            )}
            <div
                className="add-answer-button-group"
                data-testid="add-answer-button-group"
            >
                <GuxButton
                    className="add-answer"
                    data-testid="add-answer-button"
                    disabled={toGuxDisabled(disabled)}
                    aria-disabled={toGuxDisabled(disabled)}
                    onClick={() => {
                        dispatch(
                            qualityFormActions.addAnswerOption({
                                questionId: questionId,
                            })
                        );
                    }}
                >
                    <GuxIcon icon-name="add" decorative />
                    <span
                        className={`add-answer-label${
                            disabled ? "-disabled" : ""
                        }`}
                    >
                        {t.labels.addAnswer}
                    </span>
                </GuxButton>
                {disabled && (
                    <GuxTooltip>
                        <div slot="content">
                            {isCritical &&
                                t.labels.criticalQuestionAnswerLimitReached({
                                    criticalAnswerOptionCount:
                                        DefaultLimits.maxCriticalAnswerOptionCount,
                                })}
                            {hasTooManyAnswerOptions &&
                                t.labels.answerLimitReached({
                                    maxAnswerOptionCount:
                                        DefaultLimits.maxAnswerOptionCount,
                                })}
                            {hasTooManyAIScoringAnswerOptions &&
                                t.labels.aiScoringAnswerLimitReached({
                                    maxAIScoringAnswerOptionCount:
                                        DefaultLimits.maxAIScoringAnswerOptionCount,
                                })}
                        </div>
                    </GuxTooltip>
                )}
            </div>
        </PropertySection>
    );
}
